import { Table } from "@tiptap/extension-table";
import type { Node as ProseMirrorNode } from "@tiptap/pm/model";
import type { Editor } from "@tiptap/core";

const MIN_COLUMN_PX = 50;

function columnCount(node: ProseMirrorNode) {
  const row = node.firstChild;
  if (!row) return 0;
  let count = 0;
  row.forEach((cell) => {
    count += cell.attrs.colspan || 1;
  });
  return count;
}

/** Percentages that sum to 100. Saved columnSize wins; otherwise pixel colwidths become shares of the table. */
export function columnPercents(node: ProseMirrorNode) {
  const count = columnCount(node);
  if (!count) return [];

  const saved = String(node.attrs.columnSize || "")
    .split(",")
    .map((part) => parseFloat(part.trim()))
    .filter((value) => Number.isFinite(value) && value > 0);
  if (saved.length === count) return normalize(saved);

  const widths: number[] = [];
  node.firstChild?.forEach((cell) => {
    const span = cell.attrs.colspan || 1;
    const colwidth = cell.attrs.colwidth as number[] | null;
    for (let index = 0; index < span; index += 1) {
      const width = colwidth?.[index];
      widths.push(typeof width === "number" && width > 0 ? width : 0);
    }
  });
  if (widths.length === count && widths.some((width) => width > 0)) {
    const known = widths.filter((width) => width > 0);
    const average = known.reduce((total, width) => total + width, 0) / known.length;
    return normalize(widths.map((width) => width || average));
  }

  return Array(count).fill(100 / count);
}

function normalize(values: number[]) {
  const sum = values.reduce((total, value) => total + value, 0) || 1;
  const percents = values.map((value) => (value / sum) * 100);
  const rounded = percents.map((value) => Math.round(value * 100) / 100);
  const drift = Math.round((100 - rounded.reduce((total, value) => total + value, 0)) * 100) / 100;
  rounded[rounded.length - 1] = Math.round((rounded[rounded.length - 1] + drift) * 100) / 100;
  return rounded;
}

function syncCols(colgroup: HTMLTableColElement, percents: number[]) {
  while (colgroup.children.length < percents.length) {
    colgroup.appendChild(document.createElement("col"));
  }
  while (colgroup.children.length > percents.length) {
    colgroup.lastElementChild?.remove();
  }
  percents.forEach((percent, index) => {
    (colgroup.children[index] as HTMLElement).style.width = `${percent}%`;
  });
}

function createPercentTableView({
  node,
  getPos,
  editor,
  editable,
}: {
  node: ProseMirrorNode;
  getPos: () => number | undefined;
  editor: Editor;
  editable: boolean;
}) {
  let current = node;
  const dom = document.createElement("div");
  dom.className = "tableWrapper";
  const table = document.createElement("table");
  table.className = "tiptap-table";
  const colgroup = document.createElement("colgroup");
  const contentDOM = document.createElement("tbody");
  table.append(colgroup, contentDOM);
  dom.append(table);
  table.style.width = "100%";
  table.style.minWidth = "0px";
  table.style.maxWidth = "100%";

  const handles: HTMLDivElement[] = [];

  const paint = (percents: number[]) => {
    syncCols(colgroup, percents);
    if (!editable) return;
    const boundaries: number[] = [];
    percents.reduce((sum, percent, index) => {
      const next = sum + percent;
      if (index < percents.length - 1) boundaries.push(next);
      return next;
    }, 0);
    while (handles.length < boundaries.length) {
      const handle = document.createElement("div");
      handle.className = "table-column-resize";
      handle.contentEditable = "false";
      handle.addEventListener("mousedown", onMouseDown);
      dom.append(handle);
      handles.push(handle);
    }
    while (handles.length > boundaries.length) {
      handles.pop()?.remove();
    }
    boundaries.forEach((boundary, index) => {
      handles[index].style.left = `${boundary}%`;
      handles[index].dataset.index = String(index);
    });
  };

  const onMouseDown = (event: MouseEvent) => {
    if (!editable) return;
    event.preventDefault();
    event.stopPropagation();
    const handle = event.currentTarget as HTMLDivElement;
    const index = Number(handle.dataset.index);

    const onMouseMove = (move: MouseEvent) => {
      const rect = table.getBoundingClientRect();
      if (rect.width <= 0) return;
      const minPercent = (MIN_COLUMN_PX / rect.width) * 100;
      const percents = columnPercents(current);
      const boundaries = handles.map((item) => parseFloat(item.style.left));
      const prev = index === 0 ? 0 : boundaries[index - 1];
      const next = index === boundaries.length - 1 ? 100 : boundaries[index + 1];
      let percent = ((move.clientX - rect.left) / rect.width) * 100;
      percent = Math.min(Math.max(percent, prev + minPercent), next - minPercent);
      boundaries[index] = percent;
      const nextPercents = boundaries.map((boundary, boundaryIndex) => {
        const start = boundaryIndex === 0 ? 0 : boundaries[boundaryIndex - 1];
        return boundary - start;
      });
      nextPercents.push(100 - boundaries[boundaries.length - 1]);
      if (nextPercents.length !== percents.length) return;
      paint(nextPercents);
    };

    const onMouseUp = () => {
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
      const pos = getPos();
      if (typeof pos !== "number") return;
      const percents = handles.map((item, handleIndex) => {
        const boundary = parseFloat(item.style.left);
        const start = handleIndex === 0 ? 0 : parseFloat(handles[handleIndex - 1].style.left);
        return boundary - start;
      });
      percents.push(100 - parseFloat(handles[handles.length - 1].style.left));
      const columnSize = normalize(percents).join(",");
      editor.commands.command(({ tr }) => {
        const tablePos = getPos();
        if (typeof tablePos !== "number") return false;
        const tableNode = tr.doc.nodeAt(tablePos);
        if (!tableNode) return false;
        tr.setNodeMarkup(tablePos, undefined, { ...tableNode.attrs, columnSize });
        tableNode.descendants((child, offset) => {
          if (child.type.name !== "tableCell" && child.type.name !== "tableHeader") return;
          if (!child.attrs.colwidth) return;
          tr.setNodeMarkup(tablePos + 1 + offset, undefined, { ...child.attrs, colwidth: null });
        });
        return true;
      });
    };

    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
  };

  paint(columnPercents(current));

  return {
    dom,
    contentDOM,
    update(updated: ProseMirrorNode) {
      if (updated.type !== current.type) return false;
      current = updated;
      paint(columnPercents(updated));
      return true;
    },
    ignoreMutation(mutation: MutationRecord) {
      return dom.contains(mutation.target) && !contentDOM.contains(mutation.target);
    },
  };
}

export const PercentTable = Table.extend({
  addAttributes() {
    return {
      ...(this.parent?.() ?? {}),
      columnSize: {
        default: null,
        parseHTML: (element) => element.getAttribute("data-column-size") || null,
        renderHTML: (attributes) =>
          attributes.columnSize ? { "data-column-size": attributes.columnSize } : {},
      },
    };
  },
  addNodeView() {
    return ({ node, getPos, editor }) =>
      createPercentTableView({
        node,
        getPos,
        editor,
        editable: editor.isEditable,
      });
  },
}).configure({
  resizable: false,
  HTMLAttributes: {
    class: "tiptap-table",
  },
});
